import React, { useContext, useEffect, useRef, useState } from 'react';
import { PaymentRequestButtonElement, useStripe, useElements } from '@stripe/react-stripe-js';
import GlobalContext from '../contexts/store';
import { Post } from '../api/server';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { validateCheckoutBilling } from '../helpers/validateCheckoutBilling';
import { showPaymentDeclinedAlert } from "../util/showDeclinedPaymentAlert"; 

function resolveBillingForPurchase(ctx, walletEvent) {
  const payerName = (walletEvent?.payerName || '').trim();
  const nameParts = payerName ? payerName.split(/\s+/) : [];
  return {
    first_name: (ctx.firstName || '').trim() || nameParts[0] || '',
    last_name: (ctx.lastName || '').trim() || nameParts.slice(1).join(' ') || '',
    email: (ctx.email || '').trim().toLowerCase() || (walletEvent?.payerEmail || '').trim().toLowerCase(),
    phone: String(ctx.phone || '').replace(/\D/g, '') || String(walletEvent?.payerPhone || '').replace(/\D/g, ''),
  };
}

const ApplePay = ({ ticketQuantities, selectedSeatIds, totalAmountDue, event, currentTicket, handleClose, history, affId, terms, promoCode, promoCodeId, firstName, lastName, phone, email }) => {

  const { company } = useContext(GlobalContext);
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [isApplePayAvailable, setIsApplePayAvailable] = useState(false);
  const [isGooglePayAvailable, setIsGooglePayAvailable] = useState(false);

  const getBillingValidationError = () =>
    validateCheckoutBilling({
      terms,
      firstName,
      lastName,
      phone,
      email,
    });

  const handleExpressCheckoutClick = () => {
    const billingError = getBillingValidationError();
    if (billingError) {
      toast.warning(billingError);
      return;
    }
    paymentRequest?.show();
  };

  const latestRef = useRef({});
  latestRef.current = {
    ticketQuantities, selectedSeatIds, totalAmountDue, event,
    currentTicket, company, affId, terms, promoCode, promoCodeId,
    firstName, lastName, phone, email,
  };

  // Create the payment request once when Stripe is ready
  useEffect(() => {
    if (!stripe || !elements || totalAmountDue < 1 || typeof totalAmountDue === 'undefined') return;
    if (paymentRequest) return;

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: event?.name || 'Ticket(s)',
        amount: Math.round(totalAmountDue * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
    });

    pr.canMakePayment().then(result => {
      if (result) {
        setIsApplePayAvailable(result.applePay);
        setIsGooglePayAvailable(result.googlePay);
        setPaymentRequest(pr);
      }
    });

    pr.on('paymentmethod', async (e) => {
      const ctx = latestRef.current;
      const billing = resolveBillingForPurchase(ctx, e);
      const billingError = validateCheckoutBilling({
        terms: ctx.terms,
        firstName: billing.first_name,
        lastName: billing.last_name,
        phone: billing.phone,
        email: billing.email,
      });
      if (billingError) {
        e.complete('fail');
        toast.warning(billingError);
        return;
      }
      Post(`/events/v3/buyTickets/${ctx.currentTicket._id}`, {
        token: e.paymentMethod,
        company: ctx.company._id,
        first_name: billing.first_name,
        last_name: billing.last_name,
        phone: billing.phone,
        email: billing.email,
        tickets: ctx.selectedSeatIds?.length > 0 ? ctx.selectedSeatIds : ctx.ticketQuantities,
        amountDue: ctx.totalAmountDue?.toFixed(2),
        affId: ctx.affId,
        promoCode: ctx.promoCode || null,
        promoCodeId: ctx.promoCodeId || null,
      }).then(async res => {
        if (res.paymentIntent.status === 'requires_action') {
          stripe.confirmCardPayment(res.paymentIntent.client_secret, {
            payment_method: e.paymentMethod.id
          });
        }
        e.complete('success');
        navigate(`/events/confirmation/${res.user}/${res.event}`)
        ctx.currentTicket && handleClose();
      }).fail(err => {
        console.log(err);
        e.complete('fail');
        showPaymentDeclinedAlert(err);
        // toast.error(err?.responseJSON?.message || err?.responseJSON?.payment_intent?.last_payment_error?.message || 'Something went wrong on our end. Ticket(s) has not been purchased. Please try again or contact support.');
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe, elements]);

  // Update the payment request amount when totalAmountDue changes
  useEffect(() => {
    if (!paymentRequest || totalAmountDue < 1 || typeof totalAmountDue === 'undefined') return;
    paymentRequest.update({
      total: {
        label: event?.name || 'Ticket(s)',
        amount: Math.round(totalAmountDue * 100),
      },
    });
  }, [paymentRequest, totalAmountDue, event?.name]);

  return (
    <>
      {paymentRequest && (
        <>
          {(isGooglePayAvailable || isApplePayAvailable) ?  (
            <>
            <center>
              {/* Divider */}
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 border-t border-gray-300" />
                <span className="text-xs text-gray-500 uppercase tracking-widest">Express Checkout</span>
                <div className="flex-1 border-t border-gray-300" />
              </div>
              <div className="relative">
                <div className="pointer-events-none">
                  <PaymentRequestButtonElement options={{ paymentRequest }} />
                </div>
                <button
                  type="button"
                  className="absolute inset-0 z-10 w-full cursor-pointer bg-transparent"
                  aria-label="Pay with Apple Pay or Google Pay"
                  onClick={handleExpressCheckoutClick}
                />
              </div>
              {/* Divider */}
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 border-t border-gray-300" />
                <span className="text-xs text-gray-500 uppercase tracking-widest">or pay by card</span>
                <div className="flex-1 border-t border-gray-300" />
              </div>
            </center>
            </>
          ) : null}
        </>
      )}
    </>
  );
};

export default ApplePay;